<template>
    <div class="u-page-wrapper pb-56">
        <div class="u-page-headline mb-24">
            <el-row :gutter="8" class="u-text-center mb-24">
                <el-col :span="12">
                    <card>
                        <el-progress class="mb-8" type="circle" :percentage="100" :status="tireInfoStatus" />
                        <span :class="`u-size-14 text-${tireInfoStatus}`">Reifenwechsel</span>
                    </card>
                </el-col>
                <el-col :span="12">
                    <card>
                        <el-progress class="mb-8" type="circle" :percentage="100" :status="tirePressureStatus" />
                        <span :class="`u-size-14 text-${tirePressureStatus}`">Reifendruck</span>
                    </card>
                </el-col>
            </el-row>

            <div class="u-flex u-flex--space-between u-flex--vertical-center">
                <h2 class="u-size-body">
                    <i class="el-icon-document" aria-hidden="true"></i>
                    Reifen-Typ
                </h2>

                <el-button type="text" icon="el-icon-edit" circle @click="showEditModal = true" />
            </div>
        </div>

        <hint class="mb-24">Allwetterreifen ca. alle {{ changeTiresAfter }} Jahre wechseln.</hint>

        <el-row v-if="tireInfoList.length" :gutter="12" class="mb-36">
            <template v-for="(info, i) in tireInfoList">
                <el-col :key="`tireInfo-label-${i}`" :span="9" class="tire__headline">{{ info.label }}:</el-col>
                <el-col :key="`tireInfo-value-${i}`" :span="15" class="tire__value">{{ info.value }}</el-col>
            </template>
            <el-col :span="9" class="tire__headline">Erneuern:</el-col>
            <el-col v-if="nextTireChangeDate" :span="15" class="tire__value">
                {{ $timeAgo.format(nextTireChangeDate, 'round') }}
            </el-col>
        </el-row>
        <p v-else class="u-size-14 text-primary">Du hast noch keine Daten erfasst.</p>

        <div class="u-page-headline mb-24 u-flex u-flex--space-between u-flex--vertical-center">
            <h2 class="u-size-body">
                <i class="el-icon-check" aria-hidden="true"></i>
                Reifen-Check
            </h2>

            <el-button type="text" icon="el-icon-plus" circle @click="showAddPressureModal = true" />
        </div>

        <hint class="mb-24">Den Reifendruck sollte man alle zwei Wochen prüfen. Man kann 0.2 bar mehr als empfohlen befüllen.</hint>

        <template v-if="tirePressures.length">
            <el-row
                v-for="(tirePressure, i) in tirePressures"
                :key="`tirePressure-${i}`"
                class="tire-pressure-item mb-24 u-size-14"
                :gutter="12"
            >
                <el-col :span="12" class="tire__headline">Datum:</el-col>
                <el-col :span="12" class="tire__value">{{ $date(new Date(tirePressure.created), 'dd.MM.yyyy') }}</el-col>
                <el-col :span="12" class="tire__headline">Reifendruck:</el-col>
                <el-col :span="12" class="tire__value">{{ tirePressure.pressure ? `${tirePressure.pressure} bar` : 'N/A' }}</el-col>
                <el-col :span="12" class="tire__headline">Kilometerstand:</el-col>
                <el-col :span="12" class="tire__value">{{ tirePressure.mileage ? `${tirePressure.mileage} km` : 'N/A' }}</el-col>
                <el-col :span="12" class="tire__headline">Reifenprofil:</el-col>
                <el-col :span="12" class="tire__value">{{ tirePressure.profile ? `${tirePressure.profile} mm` : 'N/A' }}</el-col>
            </el-row>
        </template>
        <p v-else class="u-size-14 text-primary">Du hast noch keine Daten erfasst.</p>

        <edit-tire-type-modal
            v-if="tireInfo"
            v-model="showEditModal"
            :item="tireInfo"
            @reload="loadTireInfos"
        />
        <add-tire-pressure-modal
            v-model="showAddPressureModal"
            @reload="loadTireChecks"
        />
    </div>
</template>

<script>
import { head } from 'ramda'
import add from 'date-fns/add'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import Appwrite from '@/assets/appwrite'
import Hint from '@/components/Hint'
import EditTireTypeModal from '@/components/modals/EditTireTypeModal'
import Card from '@/components/Card'
import AddTirePressureModal from '@/components/modals/AddTirePressureModal'
import parseISO from 'date-fns/parseISO'
import format from 'date-fns/format'

export default {
    components: { AddTirePressureModal, Card, EditTireTypeModal, Hint },

    data () {
        return {
            changeTiresAfter: 3, // years

            showEditModal: false,
            showAddPressureModal: false,

            tireInfo: null,
            tirePressures: [],
        }
    },

    computed: {
        nextTireChangeDate () {
            if (!this.tireInfo) return ''

            const purchaseDate = new Date(this.tireInfo.purchase_date)
            const nextTireChangeDate = add(purchaseDate, { years: this.changeTiresAfter })

            return new Date(nextTireChangeDate)
        },

        tireInfoList () {
            if (!this.tireInfo) return []

            const data = this.tireInfo

            let type = data.type
            if (data.inch) {
                type += ` (${data.inch} Zoll)`
            }

            return [
                { label: 'Typ', value: type },
                { label: 'Reifendruck', value: data.recommend_pressure ? `${data.recommend_pressure} bar` : 'N/A' },
                { label: 'Kaufdatum', value: this.$date(new Date(data.purchase_date), 'dd.MM.yyyy') },
            ]
        },

        tireInfoStatus () {
            if (!this.tireInfo) return 'warning'

            const purchaseDate = this.tireInfo.purchase_date
            const diff = differenceInCalendarDays(new Date(), new Date(purchaseDate))

            const changeYearInDays = this.changeTiresAfter * 365

            if (diff >= changeYearInDays) return 'exception'
            if (diff >= (changeYearInDays / 1.5)) return 'warning'

            return 'success'
        },

        tirePressureStatus () {
            if (this.tirePressures.length === 0) return 'warning'

            const lastPressure = head(this.tirePressures).created
            const diff = differenceInCalendarDays(new Date(), new Date(lastPressure))

            if (diff >= 180) return 'exception'
            if (diff >= 90) return 'warning'

            return 'success'
        },
    },

    mounted () {
        this.reload()
    },

    methods: {
        reload () {
            this.loadTireInfos()
            this.loadTireChecks()
        },

        async loadTireInfos () {
            try {
                const { documents } = await Appwrite.databases.listDocuments(Appwrite.DB_NAME, 'tire_type')
                const doc = documents[0] || null
                if (!doc) return

                this.tireInfo = {
                    ...doc,
                    id: doc.$id,
                    updated: doc.$updatedAt,
                    purchase_date: format(parseISO(doc.purchase_date), 'yyyy-MM-dd'),
                }
            } catch (e) {
                this.$notify({
                    title: 'Error',
                    message: e.message,
                    type: 'error',
                })
            }
        },

        async loadTireChecks () {
            try {
                const { documents } = await Appwrite.databases.listDocuments(Appwrite.DB_NAME, 'tire_pressure', [
                    Appwrite.Query.orderDesc('$createdAt'),
                ])
                if (!documents.length) return

                this.tirePressures = documents.map(x => ({ ...x, created: x.$createdAt }))
            } catch (e) {
                this.$notify({
                    title: 'Error',
                    message: e.message,
                    type: 'error',
                })
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.tire {
    &__headline {
        margin-bottom: $spacing-unit-8;
        color: $primary-color;
    }

    &__value {
        margin-bottom: $spacing-unit-8;
    }
}

.tire-pressure-item {
    padding-bottom: $spacing-unit-16;
    border-bottom: 1px solid rgba($white, 0.2);
}
</style>
